<template>
	<ClientOnly>
		<UPopover
			:open="isSearchActive"
			:dismissible="false"
			:ui="{ content: 'w-(--reka-popper-anchor-width) ring ring-transparent text-highlighted bg-default' }"
		>
			<UForm
				:id="formId"
				:state="state"
				:validate="validate"
				class="relative flex flex-col sm:flex-row items-start gap-2 w-full justify-start max-w-3xl"
				@error="onError"
				@submit.prevent="submitAddress"
			>
				<UFormField
					label="address"
					name="address"
					:error="false"
					:ui="{
						root: 'w-full',
						label: 'hidden',
						container: 'mt-0',
						error: 'hidden'
					}"
				>
					<!-- Address Form Field -->
					<UInput
						:id="addressInputId"
						v-model="state.address"
						:label="t('address-checker.input-address.label')"
						:placeholder="isLoading ? 'Loading address search...' : t('address-checker.input-address.placeholder')"
						:loading="isLoading"
						type="text"
						size="xl"
						class="w-full flex-1 flex-grow"
						icon="i-lucide-map-pin"
						autocomplete="off"
						:ui="{
							base: 'w-full leading-6 rounded-xs border-0 placeholder:text-dimmed focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors',
							leadingIcon: 'shrink-0 text-primary'
						}"
						@focus="handleAddressInputFocus"
						@blur="handleAddressInputBlur"
						@input="trackFormEvent($event, trackingLocation)"
						@keydown="handleKeyPress"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>

				<!-- Zip Code Form Field -->
				<UFormField
					label="zipCode"
					name="zipCode"
					:error="false"
					:ui="{
						root: 'block w-full sm:w-auto sm:inline-flex',
						label: 'hidden',
						container: 'mt-0',
						error: 'hidden'
					}"
				>
					<UInput
						:id="zipInputId"
						ref="zipInput"
						v-model="state.zipCode"
						:label="t('address-checker.input-zip.label')"
						:placeholder="t('address-checker.input-zip.placeholder')"
						type="text"
						:autocomplete="ZIP_CODE_AUTOCOMPLETE"
						size="xl"
						class="block w-full sm:w-auto sm:inline-flex"
						:ui="{
							base: [
								compact ? 'sm:max-w-48' : 'sm:max-w-content',
								'w-full rounded-xs border-0 placeholder:text-dimmed focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors'
							],
							leadingIcon: 'shrink-0 text-primary'
						}"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>

				<!-- Submit Button -->
				<UButton
					:id="submitButtonId"
					type="submit"
					auto-loading
					:color="buttonColor"
					:variant="buttonVariant"
					:label="t('address-checker.button.label')"
					size="xl"
					:ui="{
						base: [
							'text-center block w-full sm:w-auto sm:inline-flex'
						]
					}"
					@click="trackFormEvent($event, trackingLocation)"
					@mouseenter="trackFormEvent($event, trackingLocation)"
					@focus="trackFormEvent($event, trackingLocation)"
					@blur="trackFormEvent($event, trackingLocation)"
				/>
			</UForm>

			<template #content>
				<Autocompleter
					class="w-full"
					:result-list="resultList"
					:is-search-active="isSearchActive && !isLoading"
					:active-search-item="activeSearchItem"
					@set-search-term="handleSetSearchTerm"
				/>
			</template>
		</UPopover>
	</ClientOnly>
</template>

<script setup lang="ts">
import { useAddressAutocompleter } from '@/composables/useAutocompleter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useProspectAddress } from '@/composables/useProspectAddress'
import { ZIP_CODE_AUTOCOMPLETE } from '@/constants/form/autocompletes.js'
import {
	ADDRESS_CHECKER_FORM_ID_BASE,
	ADDRESS_CHECKER_SUBMIT_BUTTON_ID_BASE,
	ADDRESS_INPUT_ID_BASE,
	ZIP_INPUT_ID_BASE
} from '@/constants/ga4/tracking-ids/forms/address-checker'
import type {
	ButtonProps,
	FormError,
	FormErrorEvent
} from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import * as z from 'zod'

const {
	trackError,
	trackAddressCheck,
	trackFormEvent
} = useGA4Tracking()

const { t } = useI18n({
	useScope: 'global'
})

const props = defineProps({
	trackingLocation: {
		type: String,
		required: true
	},
	buttonVariant: {
		type: String as PropType<ButtonProps['variant']>,
		default: 'solid'
	},
	buttonColor: {
		type: String as PropType<ButtonProps['color']>,
		default: 'primary'
	},
	compact: {
		type: Boolean,
		default: false
	}
})

const { trackingLocation } = toRefs(props)

const formId = computed(() => {
	return `${trackingLocation.value}${ADDRESS_CHECKER_FORM_ID_BASE}`
})
const addressInputId = computed(() => {
	return `${trackingLocation.value}${ADDRESS_INPUT_ID_BASE}`
})
const zipInputId = computed(() => {
	return `${trackingLocation.value}${ZIP_INPUT_ID_BASE}`
})
const submitButtonId = computed(() => {
	return `${trackingLocation.value}${ADDRESS_CHECKER_SUBMIT_BUTTON_ID_BASE}`
})

// Zod schema for a single address string
const addressStringSchema = z.string()
	.min(1, { message: t('address-checker.input-address.validation.required') }) // Ensures the field is not empty
	.min(10, { message: t('address-checker.input-address.validation.min-length') })

const zipCodeStringSchema = z.string()
	.min(1, { message: t('address-checker.input-zip.validation.required') }) // Ensures the field is not empty
	.regex(/^\d{5}$/, { message: t('address-checker.input-zip.validation.pattern-failed') }) // Ensures the field matches the zip code format

const schema = z.object({
	address: addressStringSchema,
	zipCode: zipCodeStringSchema
})

type Schema = z.output<typeof schema>

const state = reactive<Partial<Schema>>({
	address: undefined,
	zipCode: undefined
})

const validate = (formData: any): FormError[] => {
	const errors: FormError[] = []
	try {
		schema.parse(formData) // formData is the 'state' object from UForm
	} catch (e) {
		if (e instanceof z.ZodError) {
			e.issues.forEach(issue => {
				// Nuxt UI FormError expects 'name' for the field identifier
				errors.push({
					name: issue.path.join('.'),
					message: issue.message
				})
			})
		} else {
			// Fallback for non-Zod errors
			errors.push({
				name: 'address-checker',
				message: 'An unexpected validation error occurred.'
			})
		}
	}
	return errors
}

async function onError(event: FormErrorEvent) {
	if (event?.errors?.[0]?.id) {
		const element = document.getElementById(event.errors[0].id)
		if (element) {
			element?.focus()
			element?.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			})
		}

		trackError('Address Checker Validation Error: ' + event.errors[0].message, formId.value)
	}
}

const {
	getProspectAddressData,
	getProspectAddressWithPlaceId
} = useProspectAddress()
const route = useRoute()

const {
	searchTerm,
	activeSearchItem,
	resultList,
	isSearchActive,
	setSearchTerm,
	searchFocused,
	searchBlurred,
	handleKeyPress,
	searchPlaceId,
	selectedFromList,
	clearSearchResults,
	isLoading
} = useAddressAutocompleter()

// Sync the composable searchTerm to the form address value
watch(() => state.address, (newVal: string | undefined) => {
	if (newVal != null && newVal !== undefined) {
		searchTerm.value = newVal
	}
}
)

watch(searchTerm, (newValue: string, oldValue: string) => {
	if (!newValue.length && !!oldValue?.length) {
		setTimeout(() => {
			clearSearchResults()
		}, 250)
	}
})

// Event handlers for tracking
const handleAddressInputFocus = ($event: Event) => {
	trackFormEvent($event, trackingLocation.value)
	searchFocused()
}

const handleAddressInputBlur = ($event: Event) => {
	trackFormEvent($event, trackingLocation.value)
	searchBlurred()
}

const handleSetSearchTerm = async (searchTermParam: string, placeIdParam: string, index: number) => {
	trackAddressCheck(formId.value, 'select', state.zipCode as string, {
		location: trackingLocation.value
	})

	// Set the search term and place ID in the composable
	setSearchTerm(searchTermParam, placeIdParam, index)
	// update form address value
	state.address = searchTermParam
	// Wait for state.address update
	await nextTick()
	// Automatically fetch when an autocomplete address is selected and ZIP is present
	if (state.zipCode) {
		if (selectedFromList.value) {
			await getProspectAddressWithPlaceId(
				searchPlaceId.value,
				state.zipCode as string,
				searchTerm.value as string,
				campaignId.value as string
			)
		} else {
			await getProspectAddressData(
				searchTerm.value as string,
				state.zipCode as string,
				campaignId.value as string
			)
		}
		searchBlurred()
	}
}

// Core submission logic using current state values
const submitAddress = async () => {
	trackAddressCheck(formId.value, 'submit', state.zipCode as string, {
		location: trackingLocation.value,
		is_utm_campaign: isUtmCampaign.value,
		campaign_id: campaignId.value
	})

	if (selectedFromList.value) {
		await getProspectAddressWithPlaceId(
			searchPlaceId.value,
			state.zipCode as string,
			searchTerm.value as string,
			campaignId.value as string
		)
	} else {
		await getProspectAddressData(
			searchTerm.value as string,
			state.zipCode as string,
			campaignId.value as string
		)
	}
	searchBlurred()
}

const isUtmCampaign = computed(() => {
	return !!route?.query?.utm_source
})
const campaignId = computed(() => {
	if (isUtmCampaign.value) {
		return route?.query?.utm_campaign || null
	}
	return null
})
</script>
